import {
    daysAgo,
    formatLongDateNoTime,
    HomeCareLeadId,
    parseEntityId,
    TenantPartnershipId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { HomeCareLeadNamesHeader } from "components/clients/client-names-header";
import { HeaderNameActions } from "components/details-page-v-next/header-name-actions";
import { ShowEditDetailsButton } from "components/details-page-v-next/show-edit-details-button";
import { ClientsPage } from "components/feature";
import { ContentFull, ContentSection2Up, EntityPageHeader } from "components/layout";
import { ReadOnlyImportantNote } from "components/notes/important-note-banner";
import { trpc } from "components/rpc/trpc";
import { ActionBarValue } from "components/shared/action-bar-component";
import { DetailPageLayout } from "components/shared/detail-page-layout";
import { Subnav } from "components/shared/subnav";
import { SubnavItem } from "components/shared/subnav/subnav-types";
import { BlankSlate } from "components/ui/blank-slate";
import { InlineBanner } from "components/ui/inline-banner";
import { defaultLinkStyle } from "components/ui/link";
import { Spinner } from "components/ui/spinner";
import * as React from "react";
import { Link, useHistory, useRouteMatch } from "react-router-dom";
import { useRegionId } from "store/selectors/hooks";
import { stableQueryOptionsRpc } from "util/requests";
import { handleEvent } from "util/user-events";
import { SharedAdditionalContacts } from "./shared-additional-contacts";
import { SharedChip } from "./shared-chip";
import { SharedIntake, SharedIntakeEntity } from "./shared-intake";
import {
    hasSharedLeadDetails,
    SharedLeadDetailsBlock,
} from "./shared-lead-details-block";
import { SharedLeadFiles } from "./shared-lead-files";
import { SharedLeadFullHistory } from "./shared-lead-history";
import { isNoLongerSharedError } from "./shared-lead-load-error";
import {
    SharedLeadLocation,
    sharedLeadLocationPath,
    sharedPipelinePath,
} from "./shared-pipeline-tabs";
import { SharedPlannedActivities } from "./shared-planned-activities";
import { SharedRecentActivities } from "./shared-recent-activities";
import { useSharedPipelines } from "./use-shared-pipelines";

type SharedLeadDetail = inferProcedureOutput<AppRouter["sharedPipeline"]["lead"]>;

const none = "(none)";

const longDate = (value: string | null): string =>
    value ? formatLongDateNoTime(value) : none;

const SharedLeadByline: React.FC<{
    lead: SharedLeadDetail;
    mode: "header" | "action-bar";
}> = ({ lead, mode }) => (
    <div
        className={
            mode === "header"
                ? "hidden lg:block text-sm text-gray-500"
                : "inline-block rounded-full bg-gray-100 px-3 py-1 text-sm shadow border border-gray-200 font-medium text-gray-800"
        }>
        Last updated {daysAgo(lead.updatedAt)}
    </div>
);

const useSharesMoreThanOneOffice = (partnershipId: TenantPartnershipId): boolean => {
    const { pipelines } = useSharedPipelines();
    const pipeline = pipelines.find(
        candidate => candidate.partnershipId === partnershipId,
    );
    return (pipeline?.offices.length ?? 0) > 1;
};

const Chip: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div>
        <ActionBarValue label={label} value={value} />
    </div>
);

const SharedLeadChips: React.FC<{
    lead: SharedLeadDetail;
    partnershipId: TenantPartnershipId;
}> = ({ lead, partnershipId }) => {
    const showOffice = useSharesMoreThanOneOffice(partnershipId);
    return (
        <div className="flex flex-wrap gap-2 md:gap-3" data-testid="shared-lead-chips">
            <Chip label="Status" value={lead.statusName ?? none} />
            <Chip label="Assigned to" value={lead.assignedUserName ?? "Unassigned"} />
            {showOffice ? (
                <Chip label="Office" value={lead.regionName ?? "Unassigned"} />
            ) : null}
            <Chip
                label="Hours/week"
                value={lead.hoursPerWeek === null ? none : `${lead.hoursPerWeek}`}
            />
            <Chip label="Estimated start" value={longDate(lead.estimatedStartDate)} />
            <Chip label="Start of care" value={longDate(lead.actualStartDate)} />
            <Chip label="End of care" value={longDate(lead.endOfCareDate)} />
            <div className="inline-block lg:hidden">
                <SharedLeadByline lead={lead} mode="action-bar" />
            </div>
        </div>
    );
};

const SharedLeadOverview: React.FC<{ lead: SharedLeadDetail }> = ({ lead }) => (
    <div className="grid grid-cols-1 gap-2 lg:grid-cols-2 lg:gap-4 lg:items-start print:block">
        <div className="flex flex-col gap-2 lg:gap-4">
            <SharedPlannedActivities activities={lead.plannedActivities} />
            <SharedAdditionalContacts contacts={lead.additionalContacts} />
        </div>
        <div className="flex flex-col gap-2 lg:gap-4">
            <SharedRecentActivities history={lead.history} />
        </div>
    </div>
);

const intakeEntity = (lead: SharedLeadDetail): SharedIntakeEntity => ({
    hoursPerWeek: lead.hoursPerWeek ?? null,
    daysNeeded: lead.daysNeeded ?? null,
});

const SharedLeadIntakeTab: React.FC<{ lead: SharedLeadDetail }> = ({ lead }) =>
    lead.intake.sections.length > 0 ? (
        <SharedIntake intake={lead.intake} lead={intakeEntity(lead)} />
    ) : (
        <BlankSlate>No intake fields yet.</BlankSlate>
    );

const SharedLeadHistoryTab: React.FC<{ lead: SharedLeadDetail }> = ({ lead }) => (
    <ContentSection2Up className="mx-0!">
        <div className="flex-1 bg-white border border-gray-200 rounded-lg shadow-sm">
            <ContentFull>
                <SharedLeadFullHistory history={lead.history} />
            </ContentFull>
        </div>
        <div className="flex-1"></div>
    </ContentSection2Up>
);

const filesBadge = (lead: SharedLeadDetail): SubnavItem["badge"] =>
    lead.files.length > 0 ? { count: lead.files.length, type: "info" } : undefined;

const sharedLeadSubpages = (
    lead: SharedLeadDetail,
    location: SharedLeadLocation,
): SubnavItem[] => {
    const basePath = sharedLeadLocationPath(location);
    return [
        {
            href: `${basePath}/overview`,
            linkText: "Overview",
            subpage: "overview",
            render: () => <SharedLeadOverview lead={lead} />,
        },
        {
            href: `${basePath}/intake`,
            linkText: "Intake",
            subpage: "intake",
            render: () => <SharedLeadIntakeTab lead={lead} />,
        },
        {
            href: `${basePath}/files`,
            linkText: "Files",
            subpage: "files",
            badge: filesBadge(lead),
            render: () => <SharedLeadFiles location={location} files={lead.files} />,
        },
        {
            href: `${basePath}/history`,
            linkText: "History",
            subpage: "history",
            render: () => <SharedLeadHistoryTab lead={lead} />,
        },
    ];
};

const SharedLeadSubnav: React.FC<{
    lead: SharedLeadDetail;
    location: SharedLeadLocation;
}> = ({ lead, location }) => {
    const { params } = useRouteMatch<{ subpage?: string }>();
    const { push } = useHistory();
    const basePath = sharedLeadLocationPath(location);
    const subpages = React.useMemo(
        () => sharedLeadSubpages(lead, location),
        [lead, location],
    );
    const navigate = React.useCallback(
        (subpage: string) => push(`${basePath}/${subpage}`),
        [basePath, push],
    );
    const closeSection = React.useCallback(() => push(basePath), [basePath, push]);

    return (
        <DetailPageLayout>
            <Subnav
                subpages={subpages}
                selectedSubpage={params.subpage}
                defaultSubpage="overview"
                navigate={navigate}
                closeSection={closeSection}
            />
        </DetailPageLayout>
    );
};

const SharedLeadHeader: React.FC<{
    lead: SharedLeadDetail;
    detailsExpanded: boolean;
    onToggleDetails: () => void;
}> = ({ lead, detailsExpanded, onToggleDetails }) => {
    const tenantName = lead.sharedBy.tenantName;
    return (
        <EntityPageHeader>
            <HeaderNameActions
                name={
                    <HomeCareLeadNamesHeader
                        clientName={lead.selfContact.name ?? ""}
                        bestContactName={lead.bestContact.name ?? ""}
                    />
                }
                actions={
                    <>
                        <SharedChip label={`Shared by ${tenantName}`} />
                        <ShowEditDetailsButton
                            editing={detailsExpanded}
                            mode="view"
                            onToggle={onToggleDetails}
                        />
                    </>
                }
            />
            <div className="ml-auto">
                <SharedLeadByline lead={lead} mode="header" />
            </div>
        </EntityPageHeader>
    );
};

const SharedLeadDetails: React.FC<{
    lead: SharedLeadDetail;
    location: SharedLeadLocation;
}> = ({ lead, location }) => {
    const { partnershipId } = location;
    const [detailsExpanded, setDetailsExpanded] = React.useState(
        () => !hasSharedLeadDetails(lead),
    );
    const toggleDetails = React.useCallback(
        () => setDetailsExpanded(current => !current),
        [],
    );

    const homeCareLeadId = lead.id;
    React.useEffect(() => {
        handleEvent("shared-home-care-lead-open", { partnershipId, homeCareLeadId });
    }, [partnershipId, homeCareLeadId]);

    return (
        <div data-testid="shared-lead-details">
            <SharedLeadHeader
                lead={lead}
                detailsExpanded={detailsExpanded}
                onToggleDetails={toggleDetails}
            />
            <div className="@container space-y-2 px-4 pb-3">
                <SharedLeadDetailsBlock lead={lead} expanded={detailsExpanded} />
                <SharedLeadChips lead={lead} partnershipId={partnershipId} />
            </div>
            {lead.importantNote ? (
                <div className="px-4 pb-3">
                    <ReadOnlyImportantNote note={lead.importantNote} />
                </div>
            ) : null}
            <SharedLeadSubnav lead={lead} location={location} />
        </div>
    );
};

const unavailableMessage = (error: unknown) =>
    isNoLongerSharedError(error)
        ? { type: "warning" as const, text: "This client is no longer shared with you." }
        : {
              type: "error" as const,
              text: "This client could not be loaded. Try again in a moment.",
          };

const SharedLeadUnavailable: React.FC<{
    error: unknown;
    partnershipId: TenantPartnershipId;
}> = ({ error, partnershipId }) => {
    const message = unavailableMessage(error);
    return (
        <div className="p-4">
            <InlineBanner type={message.type}>
                {message.text}{" "}
                <Link to={sharedPipelinePath(partnershipId)} className={defaultLinkStyle}>
                    Back to the shared pipeline
                </Link>
            </InlineBanner>
        </div>
    );
};

export const SharedHomeCareLeadPage: React.FC = () => {
    const { params } = useRouteMatch<{ partnershipId: string; homeCareLeadId: string }>();
    const partnershipId = parseEntityId<TenantPartnershipId>(params.partnershipId);
    const homeCareLeadId = parseEntityId<HomeCareLeadId>(params.homeCareLeadId);
    const regionId = useRegionId();
    const location = React.useMemo(
        () => ({ regionId, partnershipId, homeCareLeadId }),
        [regionId, partnershipId, homeCareLeadId],
    );
    const trpcContext = trpc.useTRPC();
    const query = useQuery({
        ...trpcContext.sharedPipeline.lead.queryOptions({
            regionId,
            partnershipId,
            homeCareLeadId,
        }),
        ...stableQueryOptionsRpc(),
        staleTime: 60 * 1000,
        enabled: !!regionId,
        retry: false,
    });

    return (
        <ClientsPage>
            {query.isLoading || !regionId ? (
                <div className="flex items-center justify-center p-8">
                    <Spinner />
                </div>
            ) : query.data ? (
                <SharedLeadDetails
                    key={query.data.id}
                    lead={query.data}
                    location={location}
                />
            ) : (
                <SharedLeadUnavailable
                    error={query.error}
                    partnershipId={partnershipId}
                />
            )}
        </ClientsPage>
    );
};
